import { AdminGuard } from '@/components/auth/AdminGuard'
import { SectionGuard } from '@/components/auth/SectionGuard'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { RootLayout } from '@/components/layout/RootLayout'
import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Analytics = lazy(() => import('@/pages/Analytics'))
const Campaigns = lazy(() => import('@/pages/Campaigns'))
const CampaignDetail = lazy(() => import('@/pages/CampaignDetail'))
const CampaignNew = lazy(() => import('@/pages/CampaignNew'))
const Offers = lazy(() => import('@/pages/Offers'))
const OfferNew = lazy(() => import('@/pages/OfferNew'))
const OfferDetail = lazy(() => import('@/pages/OfferDetail'))
const Sites = lazy(() => import('@/pages/Sites'))
const SiteBuilder = lazy(() => import('@/pages/SiteBuilder'))
const SiteRoutes = lazy(() => import('@/pages/SiteRoutes'))
const Reports = lazy(() => import('@/pages/Reports'))
const Settings = lazy(() => import('@/pages/Settings'))
const Billing = lazy(() => import('@/pages/Billing'))
const Sources = lazy(() => import('@/pages/Sources'))
const Integrations = lazy(() => import('@/pages/Integrations'))
const Domains = lazy(() => import('@/pages/Domains'))
const Notifications = lazy(() => import('@/pages/Notifications'))
const Affiliate = lazy(() => import('@/pages/Affiliate'))

// Auth pages
const Login = lazy(() => import('@/pages/auth/Login'))
const Register = lazy(() => import('@/pages/auth/Register'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'))
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'))

// Team pages
const AcceptInvitation = lazy(() => import('@/pages/AcceptInvitation'))

// Admin pages
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'))
const AdminUserDetail = lazy(() => import('@/pages/admin/AdminUserDetail'))
const AdminTickets = lazy(() => import('@/pages/admin/AdminTickets'))
const AdminTicketDetail = lazy(() => import('@/pages/admin/AdminTicketDetail'))
const AdminInvites = lazy(() => import('@/pages/admin/AdminInvites'))
const AdminAffiliate = lazy(() => import('@/pages/admin/AdminAffiliate'))
const AdminPayouts = lazy(() => import('@/pages/admin/AdminPayouts'))

// Support pages
const UserTickets = lazy(() => import('@/pages/support/UserTickets'))
const CreateTicket = lazy(() => import('@/pages/support/CreateTicket'))
const UserTicketDetail = lazy(() => import('@/pages/support/UserTicketDetail'))

// Legal pages
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService'))
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy'))

// Error pages
const NotFound = lazy(() => import('@/pages/NotFound'))

// Suspense wrapper component
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingSpinner className="min-h-screen" />}>
      {children}
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Auth routes (no sidebar)
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'login',
            element: <SuspenseWrapper><Login /></SuspenseWrapper>
          },
          {
            path: 'register',
            element: <SuspenseWrapper><Register /></SuspenseWrapper>
          },
          {
            path: 'forgot-password',
            element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper>
          },
          {
            path: 'reset-password',
            element: <SuspenseWrapper><ResetPassword /></SuspenseWrapper>
          },
        ],
      },

      // Email verification (public — must work for both authenticated and guest users)
      {
        path: 'verify-email',
        element: <SuspenseWrapper><VerifyEmail /></SuspenseWrapper>,
      },

      // Legal pages (public, no auth required)
      {
        path: 'terms',
        element: <SuspenseWrapper><TermsOfService /></SuspenseWrapper>,
      },
      {
        path: 'privacy',
        element: <SuspenseWrapper><PrivacyPolicy /></SuspenseWrapper>,
      },

      // Accept invitation routes (no GuestGuard — must work for both authenticated and guest users)
      {
        path: 'invite/accept',
        element: <SuspenseWrapper><AcceptInvitation /></SuspenseWrapper>,
      },
      {
        path: 'accept-invitation',
        element: <SuspenseWrapper><AcceptInvitation /></SuspenseWrapper>,
      },

      // Dashboard routes (with sidebar)
      {
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <SuspenseWrapper><Dashboard /></SuspenseWrapper>
          },
          {
            path: 'analytics',
            element: <SuspenseWrapper><Analytics /></SuspenseWrapper>
          },
          {
            path: 'campaigns',
            element: <SuspenseWrapper><SectionGuard section="campaigns"><Campaigns /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'campaigns/new',
            element: <SuspenseWrapper><SectionGuard section="campaigns"><CampaignNew /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'campaigns/:id',
            element: <SuspenseWrapper><SectionGuard section="campaigns"><CampaignDetail /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'offers',
            element: <SuspenseWrapper><SectionGuard section="offers"><Offers /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'offers/new',
            element: <SuspenseWrapper><SectionGuard section="offers"><OfferNew /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'offers/:id',
            element: <SuspenseWrapper><SectionGuard section="offers"><OfferDetail /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'sources',
            element: <SuspenseWrapper><SectionGuard section="sources"><Sources /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'sites',
            element: <SuspenseWrapper><SectionGuard section="landings"><Sites /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'sites/:id/builder',
            element: <SuspenseWrapper><SectionGuard section="landings"><SiteBuilder /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'site-routes',
            element: <SuspenseWrapper><SectionGuard section="landings"><SiteRoutes /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'reports',
            element: <SuspenseWrapper><SectionGuard section="reports"><Reports /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'settings/*',
            element: <SuspenseWrapper><Settings /></SuspenseWrapper>
          },
          {
            path: 'billing',
            element: <SuspenseWrapper><SectionGuard section="billing"><Billing /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'integrations',
            element: <SuspenseWrapper><SectionGuard section="networks"><Integrations /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'domains',
            element: <SuspenseWrapper><SectionGuard section="domains"><Domains /></SectionGuard></SuspenseWrapper>
          },
          {
            path: 'notifications',
            element: <SuspenseWrapper><Notifications /></SuspenseWrapper>
          },
          {
            path: 'affiliate',
            element: <SuspenseWrapper><SectionGuard section="billing"><Affiliate /></SectionGuard></SuspenseWrapper>
          },
          // Support routes (any authenticated user)
          {
            path: 'support',
            element: <SuspenseWrapper><UserTickets /></SuspenseWrapper>
          },
          {
            path: 'support/new',
            element: <SuspenseWrapper><CreateTicket /></SuspenseWrapper>
          },
          {
            path: 'support/:id',
            element: <SuspenseWrapper><UserTicketDetail /></SuspenseWrapper>
          },
          // Admin routes (admin/superadmin only)
          {
            path: 'admin/users',
            element: <SuspenseWrapper><AdminGuard><AdminUsers /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/users/:id',
            element: <SuspenseWrapper><AdminGuard><AdminUserDetail /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/tickets',
            element: <SuspenseWrapper><AdminGuard><AdminTickets /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/tickets/:id',
            element: <SuspenseWrapper><AdminGuard><AdminTicketDetail /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/invites',
            element: <SuspenseWrapper><AdminGuard><AdminInvites /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/affiliate',
            element: <SuspenseWrapper><AdminGuard><AdminAffiliate /></AdminGuard></SuspenseWrapper>
          },
          {
            path: 'admin/payouts',
            element: <SuspenseWrapper><AdminGuard><AdminPayouts /></AdminGuard></SuspenseWrapper>
          },
        ],
      },

      // 404
      {
        path: '*',
        element: <SuspenseWrapper><NotFound /></SuspenseWrapper>
      },
    ],
  },
])

// Re-export auth guards from the component
export { AuthGuard, GuestGuard } from '@/components/auth'
